'use client'

import { useEffect, useMemo, useState } from 'react'
import Loading from '@/components/circle-loading'
import MarketTile from '@/components/offers/market-tile'
import useSWR from 'swr'
import { BarChart3, ChevronDown, SlidersHorizontal, X } from 'lucide-react'
import useEscrows from '@/utils/hooks/useEscrows'
import { API_URL } from '@/utils/constants'
import {
	BITMAP_PAGE_SIZE,
	BITMAP_PRICE_BANDS,
	BITMAP_TX_PRESETS,
	bitmapHeight,
	parseBitmapPrice
} from '@/utils/bitmap-discovery'
import {
	BITMAP_NUMERICAL_ORDER_TRAIT,
	BITMAP_NUMERICAL_ORDER_VALUES,
	expandBitmapAttributeFilters,
	matchesBitmapNumberFilters,
	splitBitmapFilters
} from '@/utils/bitmap-filters'
import TraitFilters from '@/components/trait-filters'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { buildBitmapFilterAttributes } from '@/utils/bitmap-filters'
import BitmapExplorerBar from '@/components/bitmap/bitmap-explorer-bar'
import DragScrollRow from '@/components/drag-scroll-row'
import InscriptionCardWithPreview from '@/components/inscription-card-with-preview'
import InscriptionActions from '@/components/inscription-actions'
import { Bitmap3dToggle } from '@/components/bitmap-art/bitmap-3d'
import { BitmapLiveProvider } from '@/components/bitmap-art/live-context'
import ScrollLoadMore from '@/components/scroll-load-more'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import styles from '@/app/collection/[id]/remote-collection-feed/RemoteCollectionFeed.module.css'

type Filter = { trait_type: string; values: string[] }
type Escrow = { inscription_id: string; name: string; satoshi_price: number; created?: string }
type Props = {
	onSelect: (item: any) => void
	selected: Record<string, boolean>
	onShowAll: () => void
	disablePreview?: boolean
}
const countCache = new Map<number, number>()
async function fetchBlockCount(height: number, controller: AbortController) {
	const timeout = setTimeout(() => controller.abort(), 15000)
	let count: number
	try {
		const response = await fetch(`${API_URL}/bitmap/block/${height}`, {
			signal: controller.signal
		})
		if (!response.ok || !response.headers.get('content-type')?.includes('octet-stream'))
			throw new Error('Block data unavailable')
		count = (await response.arrayBuffer()).byteLength
		if (!count) throw new Error('Block data unavailable')
	} finally {
		clearTimeout(timeout)
	}
	return count
}

async function fetchIds(url: string): Promise<string[]> {
	const r = await fetch(url)
	const data = await r.json()
	if (!r.ok) throw new Error(data.error || 'Unable to load filters.')
	return data.ids
}
function filterUrl(traits: string[]) {
	if (!traits.length) return null
	const query = new URLSearchParams()
	traits.sort().forEach((t) => query.append('trait', t))
	return `/api/bitmap/discovery?${query}`
}

/** Verify only enough matches for the visible batch; reuse immutable block counts. */
function useVerifiedCounts(items: Escrow[], threshold: string, wanted: number) {
	const [state, setState] = useState<{
		source: Escrow[]
		threshold: string
		checked: number
		matches: Escrow[]
		error: boolean
	} | null>(null)
	const [retry, setRetry] = useState(0)
	const numeric = Number(threshold)
	useEffect(() => {
		if (!numeric || numeric === 1) return
		let cancelled = false
		const controller = new AbortController()
		const run = async () => {
			const matches: Escrow[] = []
			let checked = 0
			try {
				while (checked < items.length && matches.length < wanted) {
					const batch = items.slice(checked, checked + 8)
					const counts = await Promise.all(
						batch.map(async (item) => {
							const height = bitmapHeight(item)
							if (height === null) throw new Error('Missing block height')
							if (countCache.has(height)) return countCache.get(height)!
							const count = await fetchBlockCount(height, controller)
							countCache.set(height, count)
							return count
						})
					)
					if (cancelled) return
					batch.forEach((item, i) => {
						if (counts[i] < numeric) matches.push(item)
					})
					checked += batch.length
					setState(previous => previous?.source === items && previous.threshold === threshold && previous.checked > checked
						? previous
						: { source: items, threshold, checked, matches: [...matches], error: false })
				}
				if (!cancelled) setState({ source: items, threshold, checked, matches, error: false })
			} catch {
				if (!cancelled) setState({ source: items, threshold, checked, matches, error: true })
			}
		}
		void run()
		return () => {
			cancelled = true
			controller.abort()
		}
	}, [items, threshold, numeric, wanted, retry])
	if (!numeric || numeric === 1)
		return {
			items,
			complete: true,
			loading: false,
			checked: items.length,
			error: false,
			retry: () => {}
		}
	const current = state?.source === items && state.threshold === threshold ? state : null
	const complete = !!current && current.checked >= items.length
	return {
		items: current?.matches || [],
		complete,
		loading: !current?.error && !complete && (current?.matches.length || 0) < wanted,
		checked: current?.checked || 0,
		error: !!current?.error,
		retry: () => setRetry((v) => v + 1)
	}
}

export default function BitmapMarketExplorer({
	onSelect,
	selected,
	onShowAll,
	disablePreview
}: Props) {
	const {
		data: rawEscrows,
		error: escrowError,
		isLoading: loadingEscrows,
		mutate: refreshEscrows
	} = useEscrows('bitmap')
	const { data: attributes } = useSWR(
		`${API_URL}/collection/bitmap/attributes`,
		async (url: string) => {
			const r = await fetch(url)
			if (!r.ok) throw new Error('Attributes unavailable')
			return r.json()
		},
		{ revalidateOnFocus: false }
	)
	const [filters, setFilters] = useState<Filter[]>([])
	const [tx, setTx] = useState('')
	const [search, setSearch] = useState('')
	const [sort, setSort] = useState('price')
	const [unit, setUnit] = useState<'sats' | 'BTC'>('sats')
	const [draft, setDraft] = useState({ min: '', max: '' })
	const [price, setPrice] = useState<{ min?: number; max?: number }>({})
	const [priceError, setPriceError] = useState('')
	const [visibleLimit, setVisibleLimit] = useState(BITMAP_PAGE_SIZE)
	const [expanded, setExpanded] = useState(false)
	const [supplyOpen, setSupplyOpen] = useState(false)
	const { numberFilters, attributeFilters } = useMemo(() => splitBitmapFilters(filters), [filters])
	const traitKey = filterUrl(expandBitmapAttributeFilters(attributeFilters))
	const preset = BITMAP_TX_PRESETS.find((p) => p.value === tx)
	const txKey = filterUrl(
		preset && 'traits' in preset ? [...preset.traits].map((t) => `nTx:${t}`) : []
	)
	const traits = useSWR(traitKey, fetchIds, { revalidateOnFocus: false, errorRetryCount: 1 })
	const transactions = useSWR(txKey, fetchIds, { revalidateOnFocus: false, errorRetryCount: 1 })
	const escrows = useMemo<Escrow[]>(() => {
		const seen = new Set<string>()
		return (Array.isArray(rawEscrows) ? rawEscrows : []).filter((e: Escrow) => {
			if (!e.inscription_id || !e.satoshi_price || seen.has(e.inscription_id)) return false
			seen.add(e.inscription_id)
			return true
		})
	}, [rawEscrows])
	const candidates = useMemo(() => {
		const traitIds = traitKey ? new Set(traits.data || []) : null
		const txIds = txKey ? new Set(transactions.data || []) : null
		const term = search
			.trim()
			.toLowerCase()
			.replace(/\.bitmap$/, '')
		return escrows
			.filter(
				(e) =>
					(!term || e.name.toLowerCase().includes(term)) &&
					(price.min === undefined || e.satoshi_price >= price.min) &&
					(price.max === undefined || e.satoshi_price <= price.max) &&
					matchesBitmapNumberFilters(e, numberFilters) &&
					(!traitIds || traitIds.has(e.inscription_id)) &&
					(!txIds || txIds.has(e.inscription_id))
			)
			.sort((a, b) => {
				if (sort === 'height') return (bitmapHeight(a) || 0) - (bitmapHeight(b) || 0)
				if (sort === 'newest')
					return new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime()
				return sort === 'expensive'
					? b.satoshi_price - a.satoshi_price
					: a.satoshi_price - b.satoshi_price
			})
	}, [escrows, search, price, numberFilters, traits.data, transactions.data, traitKey, txKey, sort])
	const verified = useVerifiedCounts(candidates, tx, visibleLimit + 1)
	const pendingIndex =
		loadingEscrows ||
		!!(traitKey && !traits.data && !traits.error) ||
		!!(txKey && !transactions.data && !transactions.error)
	const error = escrowError || traits.error || transactions.error || verified.error
	const items = verified.items
	const visible = items.slice(0, visibleLimit)
	const hasMore = visible.length < items.length || !verified.complete
	const loading = pendingIndex || verified.loading
	const bands = useMemo(
		() =>
			BITMAP_PRICE_BANDS.map((band) => ({
				...band,
				count: escrows.filter(
					(e) =>
						e.satoshi_price >= band.min && (band.max === undefined || e.satoshi_price <= band.max)
				).length
			})),
		[escrows]
	)
	const maxBand = Math.max(1, ...bands.map((b) => b.count))
	const resetResults = () => setVisibleLimit(BITMAP_PAGE_SIZE)
	const clear = () => {
		setFilters([])
		setTx('')
		setSearch('')
		setDraft({ min: '', max: '' })
		setPrice({})
		setPriceError('')
		resetResults()
	}
	const hasFilters = !!(
		filters.length ||
		tx ||
		search ||
		price.min !== undefined ||
		price.max !== undefined
	)
	const applyPrice = (event: React.FormEvent) => {
		event.preventDefault()
		const result = parseBitmapPrice(draft, unit)
		if (result.error) {
			setPriceError(result.error)
			return
		}
		setPrice({ min: result.min, max: result.max })
		setPriceError('')
		resetResults()
	}
	const changeUnit = (next: 'sats' | 'BTC') => {
		if (next === unit) return
		const factor = next === 'BTC' ? 1 / 1e8 : 1e8
		setDraft((previous) => ({
			min:
				previous.min && Number.isFinite(Number(previous.min))
					? String(Number((Number(previous.min) * factor).toPrecision(12)))
					: previous.min,
			max:
				previous.max && Number.isFinite(Number(previous.max))
					? String(Number((Number(previous.max) * factor).toPrecision(12)))
					: previous.max
		}))
		setUnit(next)
	}
	return (
		<section
			className={cn(styles.container, 'min-w-0 bg-background')}
			aria-label="Bitmap discovery"
		>
			<div className="space-y-3 border-b border-border p-4">
				<div className="flex flex-wrap items-center justify-between gap-3">
					<div className="flex items-center gap-2"><div className="inline-flex rounded-lg bg-muted p-1 text-xs font-medium">
						<button
							type="button"
							aria-pressed="true"
							className="rounded-md bg-background px-3 py-2 shadow-sm"
						>
							For Sale
						</button>
						<button type="button" onClick={onShowAll} className="px-3 py-2 text-muted-foreground">
							Show All
						</button>
					</div>
					<Bitmap3dToggle prominent /></div>
					<div className="relative"><select
						aria-label="Sort Bitmap listings"
						value={sort}
						onChange={(e) => {
							setSort(e.target.value)
							resetResults()
						}}
						className="h-9 min-w-0 rounded-lg border border-border bg-background appearance-none pl-3 pr-8 text-xs"
					>
						<option value="price">Lowest price</option>
						<option value="expensive">Highest price</option>
						<option value="height">Block number</option>
						<option value="newest">Recently listed</option>
					</select><ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 size-3 -translate-y-1/2" /></div>
				</div>
				<div className="flex items-center gap-2">
					<Input
						aria-label="Search Bitmap block"
						placeholder="Search block # or name…"
						value={search}
						onChange={(e) => {
							setSearch(e.target.value)
							resetResults()
						}}
						className="h-9 min-w-0 text-xs"
					/>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setExpanded((v) => !v)}
						aria-expanded={expanded}
						aria-controls="bitmap-discovery-filters"
						className="shrink-0 gap-1.5"
					>
						<SlidersHorizontal className="size-3.5" />
						Filters
					</Button>
				</div>
				<div role="group" aria-label="Bitmap numerical order" className="flex flex-wrap gap-1.5">
					{BITMAP_NUMERICAL_ORDER_VALUES.map(({ value, limit }) => {
						const active = filters.some(
							(filter) =>
								filter.trait_type === BITMAP_NUMERICAL_ORDER_TRAIT && filter.values.includes(value)
						)
						return (
							<button
								key={value}
								type="button"
								title={`Block numbers below ${limit.toLocaleString('en-US')}`}
								aria-pressed={active}
								onClick={() => {
									setFilters((previous) => {
										const current =
											previous.find((filter) => filter.trait_type === BITMAP_NUMERICAL_ORDER_TRAIT)
												?.values || []
										const values = current.includes(value)
											? current.filter((item) => item !== value)
											: [...current, value]
										return [
											...previous.filter(
												(filter) => filter.trait_type !== BITMAP_NUMERICAL_ORDER_TRAIT
											),
											...(values.length
												? [{ trait_type: BITMAP_NUMERICAL_ORDER_TRAIT, values }]
												: [])
										]
									})
									resetResults()
								}}
								className={cn(
									'rounded-md border px-2.5 py-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9E77ED]',
									active
										? 'border-[#9E77ED]/60 bg-[#9E77ED]/10 text-[#6941C6] dark:text-[#B692F6]'
										: 'border-border bg-background text-muted-foreground hover:text-foreground'
								)}
							>
								{value.replace('sub', 'Sub')}
							</button>
						)
					})}
				</div>
				<div>
					<div className="mb-2 flex flex-wrap items-baseline gap-x-2">
						<span className="text-[11px] font-medium text-foreground">Layout / transactions</span>
						<span className="text-[10px] text-muted-foreground">
							Fewer transactions, simpler shapes
						</span>
					</div>
					<DragScrollRow label="Bitmap transaction counts">
						{BITMAP_TX_PRESETS.map((p) => (
							<button
								key={p.value}
								type="button"
								title={p.hint}
								aria-pressed={tx === p.value}
								onClick={() => {
									setTx(p.value)
									resetResults()
								}}
								className={cn(
									'rounded-md border px-2.5 py-1.5 text-xs transition-colors',
									tx === p.value
										? 'border-[#9E77ED]/60 bg-[#9E77ED]/10 text-[#6941C6] dark:text-[#B692F6]'
										: 'border-border bg-background text-muted-foreground hover:text-foreground'
								)}
							>
								{p.label}
							</button>
						))}
					</DragScrollRow>
				</div>
				<BitmapExplorerBar
					filters={filters}
					onChange={(trait_type, values) => {
						setFilters((prev) => [
							...prev.filter((f) => f.trait_type !== trait_type),
							...(values.length ? [{ trait_type, values }] : [])
						])
						resetResults()
					}}
					onClear={clear}
					compact
				/>
				{expanded && (
					<div
						id="bitmap-discovery-filters"
						className="space-y-4 rounded-xl border border-border bg-white dark:bg-muted/20 p-3"
					>
						<div className="flex items-center justify-between gap-2">
							<h3 className="text-sm font-semibold">Find your Bitmap</h3>
							{hasFilters && (
								<button
									type="button"
									onClick={clear}
									className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
								>
									<X className="size-3" />
									Reset filters
								</button>
							)}
						</div>
						<form onSubmit={applyPrice} className="space-y-2">
							<div className="flex items-center justify-between">
								<span className="text-[11px] font-medium">Price range</span>
								<div className="flex rounded-md bg-muted p-0.5">
									{(['sats', 'BTC'] as const).map((u) => (
										<button
											key={u}
											type="button"
											aria-pressed={unit === u}
											onClick={() => changeUnit(u)}
											className={cn(
												'rounded px-2 py-0.5 text-[10px]',
												unit === u
													? 'bg-background text-foreground shadow-sm'
													: 'text-muted-foreground'
											)}
										>
											{u}
										</button>
									))}
								</div>
							</div>
							<div className="flex items-center gap-2">
								<Input
									aria-label={`Price from ${unit}`}
									inputMode="decimal"
									placeholder="From"
									value={draft.min}
									onChange={(e) => setDraft((v) => ({ ...v, min: e.target.value }))}
									className="h-9 min-w-0 text-xs"
								/>
								<span className="text-muted-foreground">–</span>
								<Input
									aria-label={`Price to ${unit}`}
									inputMode="decimal"
									placeholder="To"
									value={draft.max}
									onChange={(e) => setDraft((v) => ({ ...v, max: e.target.value }))}
									className="h-9 min-w-0 text-xs"
								/>
								<Button type="submit" variant="outline" size="sm" className="h-9">
									Apply
								</Button>
							</div>
							{priceError && (
								<p role="alert" className="text-xs text-red-500">
									{priceError}
								</p>
							)}
							{(price.min !== undefined || price.max !== undefined) && (
								<p className="text-[10px] text-muted-foreground">
									Applied: {(price.min || 0).toLocaleString()}–
									{price.max?.toLocaleString() || 'any'} sats
								</p>
							)}
						</form>

						<Sheet>
							<SheetTrigger asChild>
								<Button variant="outline" size="sm" className="w-full text-xs">
									All traits & epochs{filters.length ? ` (${filters.length})` : ''}
								</Button>
							</SheetTrigger>
							<SheetContent side="right" className="w-[85vw] max-w-[400px] overflow-y-auto">
								<SheetHeader>
									<SheetTitle>Bitmap traits</SheetTitle>
								</SheetHeader>
								<div className="mt-4">
									<TraitFilters
										traits={buildBitmapFilterAttributes(
											Array.isArray(attributes) ? attributes : []
										)}
										currentFilters={filters}
										onChange={(trait_type, values) => {
											setFilters((prev) => [
												...prev.filter((f) => f.trait_type !== trait_type),
												...(values.length ? [{ trait_type, values }] : [])
											])
											resetResults()
										}}
									/>
								</div>
							</SheetContent>
						</Sheet>
					</div>
				)}
				<button
					type="button"
					onClick={() => setSupplyOpen((v) => !v)}
					aria-expanded={supplyOpen}
					aria-controls="bitmap-listing-supply"
					className="flex w-full items-center justify-between gap-2 text-left text-xs"
				>
					<span className="flex items-center gap-2">
						<BarChart3 className="size-3.5 text-[#6941C6] dark:text-[#B692F6]" />
						<strong>{escrows.length ? escrows.length.toLocaleString() : '…'}</strong>
						<span className="text-muted-foreground">listed on OW</span>
					</span>
					<span className="text-muted-foreground">
						{supplyOpen ? 'Hide' : 'Price distribution'}
					</span>
				</button>
				{supplyOpen && (
					<div id="bitmap-listing-supply" className="rounded-lg border border-border p-3">
						<p className="mb-3 text-[11px] text-muted-foreground">
							Current listings by asking price · sats
						</p>
						<div className="flex items-end gap-2">
							{bands.map((band) => (
								<button
									type="button"
									key={band.label}
									aria-label={`${band.count.toLocaleString()} listings, ${
										band.label
									} sats. Filter this range`}
									onClick={() => {
										setPrice({ min: band.min, max: band.max })
										setUnit('sats')
										setDraft({
											min: String(band.min),
											max: band.max === undefined ? '' : String(band.max)
										})
										setPriceError('')
										resetResults()
									}}
									className="group min-w-0 flex-1 text-center"
								>
									<span className="mb-1 block text-[10px] tabular-nums">
										{band.count.toLocaleString()}
									</span>
									<span
										className="block rounded-t bg-[#9E77ED]/65 transition-colors group-hover:bg-[#9E77ED]"
										style={{ height: Math.max(3, (band.count / maxBand) * 56) }}
									/>
									<span className="mt-1 block whitespace-nowrap text-[9px] text-muted-foreground">
										{band.label}
									</span>
								</button>
							))}
						</div>
					</div>
				)}
			</div>
			<div id="bitmap-results" className="scroll-mt-16 p-4">
				<div
					className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs"
					aria-live="polite"
				>
					<span>
						{loading ? (
							null
						) : (
							<>
								<strong>
									{items.length.toLocaleString()}
									{!verified.complete ? '+' : ''}
								</strong>{' '}
								matches
							</>
						)}
						{!!tx && tx !== '1' && (
							<span className="ml-1 text-muted-foreground">
								· {verified.checked.toLocaleString()} block counts checked
							</span>
						)}
					</span>
					{hasFilters && (
						<button
							type="button"
							onClick={clear}
							className="text-muted-foreground underline underline-offset-2"
						>
							Clear filters
						</button>
					)}
				</div>
				{!!error && (
					<div role="alert" className="rounded-lg border border-border p-4 text-sm">
						<p>Unable to finish loading. Your filters are saved.</p>
						<Button
							variant="outline"
							size="sm"
							className="mt-2"
							onClick={() => {
								void refreshEscrows()
								void traits.mutate()
								void transactions.mutate()
								verified.retry()
							}}
						>
							Retry
						</Button>
					</div>
				)}
				{(!error || visible.length > 0) && (
					<>

						{loading && !visible.length ? (
							<div role="status" aria-label="Loading matching Bitmaps" className="flex justify-center py-8"><Loading className="!w-6 !h-6" /></div>
						) : !visible.length ? (
							<div className="py-10 text-center">
								<p className="font-semibold">No matching listings</p>
								<p className="mt-1 text-xs text-muted-foreground">
									Try a wider price range or another layout.
								</p>
								<Button variant="outline" size="sm" className="mt-3" onClick={clear}>
									Reset filters
								</Button>
							</div>
						) : (
							<div className={styles.cardsGrid}>
								<MarketTile slug="bitmap" name="BITMAP" />
								<BitmapLiveProvider value={true}>
									{visible.map((escrow) => {
										const item = {
											id: escrow.inscription_id,
											content_type: 'text/plain',
											meta: { name: escrow.name, attributes: [] },
											collection: { name: 'BITMAP', slug: 'bitmap' },
											escrow
										}
										return (
											<InscriptionCardWithPreview
												key={item.id}
												inscription={item as any}
												onClick={() => onSelect(item)}
												selected={selected[item.id]}
												disablePreview={disablePreview}
												footer={
													<InscriptionActions
														inscription={item as any}
														owner={false}
														actionButtonColor="black"
														onlyShowPrice
														hideView
														collectionCard={{ slug: 'bitmap', name: 'BITMAP' }}
														onClickBuy={() => onSelect(item)}
														selected={selected[item.id]}
													/>
												}
											/>
										)
									})}
								</BitmapLiveProvider>
							</div>
						)}
						<ScrollLoadMore
							hasMore={!error && hasMore}
							loading={!error && loading}
							itemCount={visible.length}
							onLoadMore={() => setVisibleLimit(limit => limit + BITMAP_PAGE_SIZE)}
							label="Loading more Bitmaps…"
						/>
						{!error && !loading && !hasMore && visible.length > 0 && (
							<p className="py-5 text-center text-xs text-muted-foreground">All matching listings loaded</p>
						)}
					</>
				)}
			</div>
		</section>
	)
}
